import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"
import Sortable from "sortablejs"

// Connects to data-controller="report-columns-sortable"
//
// Replaces jQuery UI Sortable for drag-and-drop report column editor
//
// Features:
// - Multi-list drag & drop (available columns → target list)
// - Cloning for reusable columns (formulas, inputs, cloneable data columns)
// - Type-based filtering (only valid column types accepted in each list)
// - Automatic scroll handling (FIXES SCROLL POSITION BUG!)
// - Position tracking for form submission
//
// Usage:
//   .column-list[
//     data-controller="report-columns-sortable"
//     data-report-columns-sortable-list-id-value="reportColumnsList"
//     data-report-columns-sortable-accepts-value='["ReportColumn::Input"]'
//     data-report-columns-sortable-cloneable-value="true"
//   ]
//
export default class extends Controller {
  static values = {
    listId: String,        // Unique ID: "reportColumnsList", "availableColumnsList", etc.
    accepts: Array,        // Accepted types (empty = accept all)
    cloneable: Boolean     // Is this a source list for cloning?
  }

  connect() {
    // Check if we're inside an element that's being dragged
    // If so, DON'T initialize sortable yet - wait until drag completes
    const isDragging = this.element.closest('[data-sortable-dragging="true"]')
    if (isDragging) {
      this.shouldInitializeOnNextConnect = true
      return
    }

    // If we were skipped before and reconnecting after drag, initialize now
    if (this.shouldInitializeOnNextConnect) {
      this.shouldInitializeOnNextConnect = false
    }

    // Sortable.js loaded via importmap
    if (typeof Sortable === 'undefined') {
      logger.error('[Report Columns Sortable] ERROR: Sortable.js library not loaded')
      return
    }

    this.initializeSortable()
  }

  disconnect() {
    // Check if we're inside an element that's being dragged
    // If so, DON'T destroy sortable - parent drag is in progress
    const isDragging = this.element.closest('[data-sortable-dragging="true"]')
    if (isDragging) {
      return
    }

    // DON'T destroy sortable if we're being disconnected because we skipped init during drag
    if (this.shouldInitializeOnNextConnect) {
      return
    }

    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  initializeSortable() {
    const isTargetList = this.listIdValue === 'reportColumnsList'

    this.sortable = Sortable.create(this.element, {
      sort: isTargetList,  // Only allow sorting/reordering in reportColumnsList, not in library
      group: {
        name: 'report-columns',
        pull: (to, from, dragEl, evt) => {
          // NEW LOGIC: 
          // 1. PREVENT dragging from reportColumnsList back to available columns (use remove button instead)
          // 2. Clone only when dropping cloneable item from available columns TO reportColumnsList
          // 3. Allow reordering within reportColumnsList

          const toList = to?.el
          const fromList = from?.el

          const isToReportColumnsList = toList?.id === 'reportColumnsList' ||
                                        toList?.closest('.column-list')?.id === 'reportColumnsList'
          const isFromReportColumnsList = fromList?.id === 'reportColumnsList' ||
                                          fromList?.closest('.column-list')?.id === 'reportColumnsList'

          const isCloneable = dragEl.classList.contains('cloneable')

          // PREVENT dragging FROM reportColumnsList TO available columns
          if (isFromReportColumnsList && !isToReportColumnsList) {
            console.log('[Report Columns Sortable] ⛔ Preventing drag from selected columns back to library')
            return false
          }

          // Only clone when: cloneable item + from available columns + to reportColumnsList
          if (isCloneable && !isFromReportColumnsList && isToReportColumnsList) {
            return 'clone'
          }

          // Everything else: MOVE (allow reordering within reportColumnsList)
          return true
        },
        put: (to, from, dragEl) => {
          // Column library lists are ONLY drag sources, never targets
          // Only reportColumnsList accepts drops
          const toList = to?.el
          const isToReportColumnsList = toList?.id === 'reportColumnsList' ||
                                        toList?.id === 'selectedColumnsList'

          // If target is NOT reportColumnsList, reject the drop
          if (!isToReportColumnsList) {
            console.log('[Report Columns Sortable] ⛔ Preventing drop - only selected columns list accepts drops')
            return false
          }

          // For reportColumnsList, validate type acceptance
          const targetController = this.application.getControllerForElementAndIdentifier(
            to.el.closest('[data-controller~="report-columns-sortable"]'),
            'report-columns-sortable'
          )

          if (!targetController || !targetController.hasAcceptsValue) {
            return true  // No restrictions
          }

          const accepts = targetController.acceptsValue
          if (accepts.length === 0) {
            return true  // Empty array = accept all
          }

          const itemType = dragEl.dataset.itemType
          return accepts.includes(itemType)
        }
      },

      animation: 150,
      handle: '.handle',
      draggable: '.column-item',  // Specify which items are draggable
      dragClass: 'sortable-drag',
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',

      // SCROLL BUG FIX: Sortable.js automatically handles window scroll offset! 🎉
      scroll: true,
      scrollSensitivity: 100,
      scrollSpeed: 20,
      bubbleScroll: true,

      forceFallback: false,      // Use native drag events (better performance)
      fallbackOnBody: true,       // Append ghost to body (for scrolling)
      swapThreshold: 0.65,        // Percentage of target to trigger swap

      onStart: (evt) => {
        this.handleDragStart(evt)
      },

      onEnd: (evt) => {
        this.handleDragEnd(evt)
      },

      onClone: (evt) => {
        this.handleClone(evt)
      },

      onAdd: (evt) => {
        this.handleAdd(evt)
      },

      onRemove: (evt) => {
        this.handleRemove(evt)
      }
    })
  }

  handleDragStart(evt) {
    console.log('[Report Columns Sortable] 🎯 DRAG START!', {
      item: evt.item,
      from: evt.from,
      fromId: evt.from.id
    })

    // Mark the dragged item so nested controllers know not to reinitialize
    evt.item.dataset.sortableDragging = 'true'

    // NOTE: No need to dispatch drag-start event anymore - placeholder is transparent!

    // Prevent Bootstrap collapse from interfering with drag
    const collapseToggles = evt.item.querySelectorAll('[data-bs-toggle="collapse"]')
    collapseToggles.forEach(toggle => {
      toggle.style.pointerEvents = 'none'
    })

    // Add visual feedback for invalid targets
    document.querySelectorAll('[data-controller~="report-columns-sortable"]').forEach(el => {
      const controller = this.application.getControllerForElementAndIdentifier(
        el,
        'report-columns-sortable'
      )

      if (controller && controller.hasAcceptsValue && controller.acceptsValue.length > 0) {
        const itemType = evt.item.dataset.itemType
        const card = el.closest('.card')

        if (!controller.acceptsValue.includes(itemType)) {
          // Mark list as disabled (visual overlay via CSS)
          if (card) {
            card.classList.add('disabled')
          }
        } else {
          // Auto-expand valid targets
          if (card) {
            const collapse = card.querySelector('.collapse')
            if (collapse && !collapse.classList.contains('show')) {
              // Use Bootstrap 5 vanilla JS API
              const bsCollapse = new bootstrap.Collapse(collapse, { toggle: true })
            }
          }
        }
      }
    })
  }

  handleDragEnd(evt) {
    logger.log('[Report Columns Sortable] Drag ended!', {
      item: evt.item,
      from: evt.from.id,
      to: evt.to.id,
      oldIndex: evt.oldIndex,
      newIndex: evt.newIndex
    })

    // Remove the dragging marker so nested controllers can reinitialize
    delete evt.item.dataset.sortableDragging

    // Re-enable collapse toggles
    const collapseToggles = evt.item.querySelectorAll('[data-bs-toggle="collapse"]')
    collapseToggles.forEach(toggle => {
      toggle.style.pointerEvents = ''
    })

    // Remove disabled class from all lists
    document.querySelectorAll('[data-controller~="report-columns-sortable"]').forEach(el => {
      el.closest('.card')?.classList.remove('disabled')
    })

    // Recalculate positions for target list
    if (this.listIdValue === 'reportColumnsList') {
      this.recalculatePositions()
    }

    // Notify selected-columns controller to update empty state
    this.dispatchSelectedColumnsUpdate()

    // NOTE: No need to dispatch drag-end event anymore - placeholder is transparent!

    // Trigger auto-save after reordering
    const customEvent = new CustomEvent('report-autosave:schedule', {
      bubbles: true,
      detail: { source: 'sortable' }
    })
    this.element.dispatchEvent(customEvent)
  }

  handleClone(evt) {
    const clone = evt.clone

    // With the new pull logic, cloning ONLY happens when dragging
    // cloneable items from available columns TO reportColumnsList
    // So we always need to add a UID here

    logger.log('[Report Columns Sortable] handleClone: Cloning from available columns to reportColumnsList, adding UID')

    // CRITICAL: Use timestamp-only for Rails nested attributes compatibility
    // Rails strong parameters only accept numeric array indices
    const uid = Date.now()

    // Update all IDs/names to make clone unique
    this.updateCloneAttributes(clone, uid)
  }

  handleAdd(evt) {
    const item = evt.item
    // V3: The selected columns list has DOM id="selectedColumnsList"
    const isTargetList = evt.to.id === 'selectedColumnsList' ||
                        evt.to.id === 'reportColumnsList' ||
                        evt.to.closest('.column-list')?.id === 'reportColumnsList'
    const isFromTargetList = evt.from.id === 'selectedColumnsList' ||
                            evt.from.id === 'reportColumnsList' ||
                            evt.from.closest('.column-list')?.id === 'reportColumnsList'

    logger.log('[Report Columns Sortable] handleAdd:', {
      from: evt.from.id || evt.from.closest('.column-list')?.id,
      to: evt.to.id || evt.to.closest('.column-list')?.id,
      isFromTargetList,
      isTargetList,
      isCloneable: item.classList.contains('cloneable'),
      itemClasses: item.className
    })

    // If moving FROM reportColumnsList TO a source list, keep visible
    if (isFromTargetList && !isTargetList) {
      logger.log('[Report Columns Sortable] Item moved from reportColumnsList to source list, keeping visible')

      // Set _destroy flag - this will be handled by the general logic below
      // Whether it has a database ID or not, we want it to stay visible in available columns
      // If it has an ID: Rails will delete it on save
      // If it doesn't have an ID: Rails will ignore it (not in params from reportColumnsList)
    }

    // V3 ONLY: If dragging from available columns (.column-library-item) to selected columns,
    // we need to transform the HTML structure from the compact library format to the full form format
    if (isTargetList && item.classList.contains('column-library-item')) {
      logger.log('[Report Columns Sortable] V3: Transforming column-library-item to selected-column-item-v3')
      
      // Find the column-actions controller on this item and trigger addColumn
      // This will create the proper HTML structure
      const columnActionsController = this.application.getControllerForElementAndIdentifier(
        item.querySelector('[data-controller="column-actions"]'),
        'column-actions'
      )
      
      if (columnActionsController) {
        // Get the drop position from the event
        const dropIndex = evt.newIndex
        
        // CLONE BEHAVIOR: Move the item back to its original position in the available list
        // This ensures the item stays in the available columns (clone behavior)
        if (evt.from && evt.oldIndex !== undefined) {
          logger.log('[Report Columns Sortable] V3: Moving item back to available list (clone behavior)')
          if (evt.oldIndex < evt.from.children.length) {
            evt.from.insertBefore(item, evt.from.children[evt.oldIndex])
          } else {
            evt.from.appendChild(item)
          }
        }
        
        // Trigger the add button to create the proper structure at the drop position
        columnActionsController.addColumn({ preventDefault: () => {} }, dropIndex)
        
        logger.log('[Report Columns Sortable] V3: Created new column via column-actions at index', dropIndex)
        return // Exit early, the addColumn method handles everything
      } else {
        logger.log('[Report Columns Sortable] V3: WARNING - No column-actions controller found, item may have wrong structure')
      }
    }
    
    // If adding a cloneable item to reportColumnsList, check for duplicates
    if (isTargetList && item.classList.contains('cloneable')) {
      const columnRefInput = item.querySelector('input[name$="[column_ref]"]')
      if (columnRefInput) {
        const columnRef = columnRefInput.value

        // Check if another item in reportColumnsList has the same column_ref
        const targetList = evt.to
        const existingItems = Array.from(targetList.querySelectorAll('.column-item'))
        const hasDuplicate = existingItems.some(existingItem => {
          if (existingItem === item) return false // Skip self
          const existingRefInput = existingItem.querySelector('input[name$="[column_ref]"]')
          return existingRefInput && existingRefInput.value.startsWith(columnRef)
        })

        // If duplicate found, ensure this item has a unique ID
        if (hasDuplicate && !columnRef.match(/_\d+$/)) {
          logger.log('[Report Columns Sortable] Duplicate cloneable item detected, assigning unique ID')
          // CRITICAL: Use timestamp-only for Rails nested attributes compatibility
          const uid = Date.now()
          this.updateCloneAttributes(item, uid)
        }
      }
    }

    // Set _destroy field based on target list
    // Items in reportColumnsList are included (_destroy = false)
    // Items in other lists are excluded (_destroy = true)
    const destroyInput = item.querySelector('input[name$="[_destroy]"]')
    if (destroyInput) {
      destroyInput.value = !isTargetList
    }

    // CRITICAL: When moving TO reportColumnsList, remove stale database ID
    // This handles the case where an item was previously in the report, then removed,
    // and is now being added back. We want Rails to create a NEW record, not try to
    // update/delete the old one.
    if (isTargetList && isFromTargetList === false) {
      const idInput = item.querySelector('input[name$="[id]"]')
      if (idInput && idInput.value) {
        logger.log('[Report Columns Sortable] Removing stale database ID when re-adding to reportColumnsList')
        idInput.remove()
      }
    }

    // Collapse/expand based on target list
    if (isTargetList) {
      // Expand title field when dropped to target list
      item.querySelectorAll('.collapse-primary').forEach(el => {
        el.classList.add('show')
      })

      // Keep secondary fields collapsed
      item.querySelectorAll('.collapse-secondary').forEach(el => {
        el.classList.remove('show')
      })
    } else {
      // Collapse all fields when dropped to available list
      item.querySelectorAll('.collapse').forEach(el => {
        el.classList.remove('show')
      })
    }

    // Recalculate positions if target list
    if (isTargetList) {
      this.recalculatePositions()
    }
  }

  handleRemove(evt) {
    // Recalculate positions after item removed from target list
    if (this.listIdValue === 'reportColumnsList') {
      this.recalculatePositions()
    }
  }

  updateCloneAttributes(clone, uid) {
    logger.log('[Report Columns Sortable] Updating clone attributes with UID:', uid)

    // CRITICAL: Only update what's necessary for Rails form submission and formula references
    // DO NOT update DOM IDs (collapse, color picker, etc.) - causes duplicate ID issues

    // 1. Remove the database ID field so Rails treats this as a NEW record
    const idInput = clone.querySelector('input[name$="[id]"]')
    if (idInput) {
      logger.log('[Report Columns Sortable] Removing database ID from clone to force new record creation')
      idInput.remove()
    }

    // 2. Update Rails nested attributes index (form field names)
    // Example: report[report_sheets_attributes][0][report_columns_attributes][123][title]
    // Becomes:  report[report_sheets_attributes][0][report_columns_attributes][1697284829473][title]
    clone.querySelectorAll('input, textarea, select').forEach(el => {
      if (el.name) {
        el.name = el.name.replace(/(.*]\[)(\d+)(]\[.*)/, `$1${uid}$3`)
      }
    })

    // 3. Update column_ref (for formula references ONLY)
    // This is what formulas use to reference other columns (e.g., %{column_ref}% → Excel address)
    const columnRefInput = clone.querySelector('input[name$="[column_ref]"]')
    if (columnRefInput && columnRefInput.value) {
      // Replace existing UID suffix (if any) with new UID
      const existingRef = columnRefInput.value
      if (existingRef.match(/_\d+(_[a-z0-9]+)?$/)) {
        // Remove old UID suffix and add new one
        const newRef = existingRef.replace(/_\d+(_[a-z0-9]+)?$/, '') + '_' + uid
        logger.log('[Report Columns Sortable] Replacing column_ref:', existingRef, '→', newRef)
        columnRefInput.value = newRef
      } else {
        // No existing UID, just append
        const newRef = existingRef + '_' + uid
        logger.log('[Report Columns Sortable] Appending to column_ref:', existingRef, '→', newRef)
        columnRefInput.value = newRef
      }
    }

    // 4. Update collapse panel IDs (Bootstrap collapse system)
    // This prevents duplicate IDs when multiple clones exist
    clone.querySelectorAll('.collapse, input[name$="[title]"]').forEach(el => {
      if (el.id) {
        if (el.id.match(/_\d+(_[a-z0-9]+)?$/)) {
          el.id = el.id.replace(/_\d+(_[a-z0-9]+)?$/, '') + '_' + uid
        } else {
          el.id += '_' + uid
        }
      }
    })

    // 5. Update collapse toggle hrefs (Bootstrap data-toggle links)
    clone.querySelectorAll('a[data-bs-toggle="collapse"]').forEach(el => {
      const href = el.getAttribute('href')
      if (href && href.startsWith('#')) {
        if (href.match(/_\d+(_[a-z0-9]+)?$/)) {
          el.setAttribute('href', href.replace(/_\d+(_[a-z0-9]+)?$/, '') + '_' + uid)
        } else {
          el.setAttribute('href', href + '_' + uid)
        }
      }

      // Update aria-controls attribute
      const controls = el.getAttribute('aria-controls')
      if (controls) {
        if (controls.match(/_\d+(_[a-z0-9]+)?$/)) {
          el.setAttribute('aria-controls', controls.replace(/_\d+(_[a-z0-9]+)?$/, '') + '_' + uid)
        } else {
          el.setAttribute('aria-controls', controls + '_' + uid)
        }
      }
    })

    // 6. Update color picker data attributes
    clone.querySelectorAll('[data-color-picker-target-value]').forEach(el => {
      const currentTarget = el.dataset.colorPickerTargetValue
      if (currentTarget.match(/_\d+(_[a-z0-9]+)?$/)) {
        el.dataset.colorPickerTargetValue = currentTarget.replace(/_\d+(_[a-z0-9]+)?$/, '') + '_' + uid
      } else {
        el.dataset.colorPickerTargetValue = currentTarget + '_' + uid
      }
    })

    // 7. Update QTip tooltip data attributes (legacy tooltips)
    clone.querySelectorAll('[data-qtip-content]').forEach(el => {
      const content = el.dataset.qtipContent
      if (content) {
        if (content.match(/_\d+(_[a-z0-9]+)?$/)) {
          el.dataset.qtipContent = content.replace(/_\d+(_[a-z0-9]+)?$/, '') + '_' + uid
        } else {
          el.dataset.qtipContent = content + '_' + uid
        }
      }
    })

    // 8. Tooltip data attributes removed - no longer needed
  }

  recalculatePositions() {
    // Only for target list (reportColumnsList)
    if (this.listIdValue !== 'reportColumnsList') {
      return
    }

    // Update position hidden field for each column item
    // Skip destroyed columns so positions are contiguous
    let visibleIndex = 0
    this.element.querySelectorAll('.column-item').forEach((item) => {
      const destroyField = item.querySelector('input[name*="[_destroy]"]')
      const isDestroyed = destroyField && destroyField.value === 'true'

      const positionInput = item.querySelector('input[name$="[position]"]')
      if (positionInput) {
        if (isDestroyed) {
          positionInput.value = 99999
        } else {
          positionInput.value = visibleIndex
          visibleIndex++
        }
      }
    })
  }

  dispatchSelectedColumnsUpdate() {
    // Notify the selected-columns controller to update empty state
    console.log('[Report Columns Sortable] 📢 Dispatching selected-columns:update from:', this.element)
    const event = new CustomEvent('selected-columns:update', {
      bubbles: true,
      detail: { source: 'sortable' }
    })
    this.element.dispatchEvent(event)
    console.log('[Report Columns Sortable] ✅ selected-columns:update dispatched')
  }

  dispatchDragStart() {
    // Notify the selected-columns controller that drag started
    console.log('[Report Columns Sortable] 🚀 Creating drag-start event on element:', this.element)
    const event = new CustomEvent('selected-columns:drag-start', {
      bubbles: true,
      detail: { source: 'sortable' }
    })
    console.log('[Report Columns Sortable] 📤 Dispatching drag-start event:', event)
    this.element.dispatchEvent(event)
    console.log('[Report Columns Sortable] ✅ drag-start event dispatched')
  }

  dispatchDragEnd() {
    // Notify the selected-columns controller that drag ended
    console.log('[Report Columns Sortable] 🏁 Creating drag-end event on element:', this.element)
    const event = new CustomEvent('selected-columns:drag-end', {
      bubbles: true,
      detail: { source: 'sortable' }
    })
    console.log('[Report Columns Sortable] 📤 Dispatching drag-end event:', event)
    this.element.dispatchEvent(event)
    console.log('[Report Columns Sortable] ✅ drag-end event dispatched')
  }
}
